import { ComponentChildren, Fragment, FunctionalComponent } from 'preact';
import { Suspense, lazy } from 'preact/compat';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState, useRef } from 'preact/hooks';
import cn from 'classnames';
import ErrorBoundary from '../../ErrorBoundary';
import { useTransition, animated } from '@react-spring/web';
import { selectOnboardingStep, hideOnboardingStep } from '../user/slice';

import styles from './style.module.scss';
import {
  clickedCustomize,
  setDefault,
  selectBackgroundPicture,
  selectUi,
  prepareNextPicture,
  toggleUiElements,
  fetchBackgrounds,
  selectedCollection,
  // selectActiveView,
  selectVideoBackground,
  updateVideo,
  selectBackgroundCategory,
  resetImageViewCount,
  prepareNextVideo,
} from './slice';
// import { DEFAULT_PICTURE } from './slice/dict';
import { Picture } from './slice/types';

import {
  capitalize,
  getRandomVideo,
  getResolutionUrl,
  preloadPicture,
  // preloadVideo,
} from './slice/utils';
// import UserRating from './components/UserRating';
// import BackgroundLock from './components/BackgroundLock';
import useOnClickOutside from '../../../shared/hooks/useOutsideClick';

import SyncIcon from '@material-ui/icons/Sync';
import CustomizeIcon from '@material-ui/icons/Create';
import { selectFeatureFlags } from '../../store/selectors';
import { GlobalState } from '../../types.global';
import News from '../news';

const CustomizePopup = lazy(() => import('./components/CustomizePopup'));

const buttonNames = {
  ocean: 'Ocean mode',
  landscape: 'Incredible Places',

  forest: 'Peaceful Forests',
  city: 'Magical Towns',
  animals: 'Wild Animals',
  cats: 'Lovely Cats',
  dogs: 'Happy Dogs',

  dark: 'Simply dark',
};

const Ui: FunctionalComponent = ({
  children,
}: {
  children: ComponentChildren;
}) => {
  const CUSTOMIZE_ALTERNATIVE_LOOK = useSelector(
    (state: GlobalState) => selectFeatureFlags(state).CUSTOMIZE_ALTERNATIVE_LOOK
  );

  const [backgrounds, setBackgrounds] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchBackgrounds().then(setBackgrounds);
  }, []);

  const activeCategory = useSelector(selectBackgroundCategory);
  const { videoBackground } = useSelector(selectVideoBackground);

  const selectBackgroundImage = (
    picture: Picture,
    category,
    rotation = null
  ): void => {
    dispatch(
      selectedCollection({
        rotation,
        category,
        now: Date.now(),
        ...(picture && {
          picture: {
            ...picture,
            originUrl: picture.url,
            url: getResolutionUrl(
              window.screen.width,
              backgrounds.resolutions,
              picture.url
            ),
          },
        }),
      })
    );
  };

  const backgroundPicture = useSelector(selectBackgroundPicture);
  const onboardingStep = useSelector(selectOnboardingStep);
  const {
    mode,
    customizeOpened,
    customizeClicked,
    isDefaultSettings,
    hideUi,
    gallery,
  } = useSelector(selectUi);
  const isDefaultSettingsRef = useRef(isDefaultSettings);

  useEffect(() => {
    if (!isDefaultSettings && isDefaultSettingsRef.current) {
      isDefaultSettingsRef.current = false;
    }
  }, [isDefaultSettings]);

  const dispatch = useDispatch();

  const [displayedPicture, setDisplayedPicture] = useState<Picture | null>(
    backgroundPicture
  );
  const popupRef = useRef<HTMLDivElement>(null);

  const buttonLabel = (() => {
    if (videoBackground.show) return 'videos';

    if (mode === 'custom') return 'custom picture';

    if (mode === 'gallery') {
      return buttonNames[gallery.category] || gallery.category;
    }

    if (['speed', 'focus'].includes(mode)) return `${mode} mode`;

    return buttonNames[mode];
  })();

  useEffect(() => {
    setDisplayedPicture(backgroundPicture);
  }, [backgroundPicture]);

  // const handleError = (): void => {
  //   console.log('handleError', displayedPicture);
  //   if (displayedPicture && displayedPicture.url !== DEFAULT_PICTURE.url) {
  //     setDisplayedPicture(DEFAULT_PICTURE);
  //   }
  // };

  const handleCustomizeClick = (): void => {
    if (onboardingStep === 'customizeBg') {
      dispatch(hideOnboardingStep({ step: onboardingStep }));
    }
    dispatch(clickedCustomize());
  };

  const handleClickOutside = (): void => {
    if (customizeOpened) handleCustomizeClick();
  };

  const goBack = (): void => {
    dispatch(setDefault());
  };

  const toggleUi = (value: boolean): void => {
    dispatch(toggleUiElements(value));
  };

  const setRandomBackground = async () => {
    if (isUpdating) return;

    setIsUpdating(true);

    if (videoBackground.show) {
      const { url, newIndex, creditUrl, title, subtitle, id } =
        await getRandomVideo(videoBackground.index);

      dispatch(
        updateVideo({
          url,
          index: newIndex,
          credit: { title: subtitle, url: creditUrl },
          title: title,
          id: id,
        })
      );

      setIsUpdating(false);
      return;
    }

    const getRandomPicture = (category: string): Picture => {
      const pictures = backgrounds.categories[category];
      const index = Math.floor(Math.random() * pictures.length);
      return pictures[index];
    };

    const category = activeCategory;

    const picture = getRandomPicture(category);

    await preloadPicture(
      getResolutionUrl(
        window.screen.width,
        backgrounds.resolutions,
        picture.url
      )
    );

    selectBackgroundImage(picture, category, gallery.rotation);

    // Reset the image view count to 0
    resetImageViewCount();

    setIsUpdating(false);
  };

  useEffect(() => {
    dispatch(prepareNextPicture());
    dispatch(prepareNextVideo());
  }, []);

  useEffect(() => {
    if (onboardingStep === 'customizeBg') {
      dispatch(clickedCustomize());
    }
  }, [onboardingStep]);

  useOnClickOutside(
    popupRef,
    handleClickOutside,
    onboardingStep === 'customizeBg'
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const transition = useTransition(videoBackground, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 1000 },
  });

  return (
    <>
      <div className={styles.body}>
        <section className={styles.bgPicture} id="bg-picture">
          {/* <img
            alt=""
            {...(loadedImage &&
              typeof window !== 'undefined' && { src: loadedImage })}
            className={styles.bgImage}
            key={loadedImage}
          /> */}
          {transition((style, item) => {
            return (
              item.show && (
                <animated.video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={styles.bgVideo}
                  src={item.url}
                  key={item.url}
                  style={style}
                />
              )
            );
          })}

          {!videoBackground?.show && mounted && (
            <div
              style={{ backgroundImage: `url(${displayedPicture?.url})` }}
              className={styles.bgImage}
            />
          )}
        </section>

        <script src={`${process.env.PUBLIC_PATH}bg.js`} />

        {displayedPicture && typeof window !== 'undefined' && (
          <Fragment>
            <div
              className={styles.bgCredits}
              onMouseEnter={(): void => toggleUi(true)}
              onMouseLeave={(): void => toggleUi(false)}
            >
              {/* {mode === 'gallery' &&
                displayedPicture.url !== DEFAULT_PICTURE.url && (
                  <div className={styles.rotatingBgControls}>
                    <UserRating />
                    {mode === 'gallery' && <BackgroundLock />}
                  </div>
                )} */}
              {videoBackground.show ? (
                !videoBackground?.creditUrl ? (
                  <div className={styles.creditsLink}>
                    <div className={`${styles.title} ${styles.bgTitle}`}>
                      {capitalize(videoBackground.title)}
                    </div>
                    <span className={`${styles.author} ${styles.creditsTitle}`}>
                      {capitalize(videoBackground.credit.title)}
                    </span>
                  </div>
                ) : (
                  <a
                    className={styles.creditsLink}
                    href={videoBackground.creditUrl}
                  >
                    <div className={`${styles.title} ${styles.bgTitle}`}>
                      {capitalize(videoBackground.title)}
                    </div>
                    <span className={`${styles.author} ${styles.creditsTitle}`}>
                      {capitalize(videoBackground.credit.title)}
                    </span>
                  </a>
                )
              ) : (
                displayedPicture.credit?.title &&
                (!displayedPicture.credit.url ? (
                  <div className={styles.creditsLink}>
                    <div className={`${styles.title} ${styles.bgTitle}`}>
                      {capitalize(displayedPicture.title)}
                    </div>
                    <span className={`${styles.author} ${styles.creditsTitle}`}>
                      {capitalize(displayedPicture.credit.title)}
                    </span>
                  </div>
                ) : (
                  <a
                    className={styles.creditsLink}
                    href={displayedPicture.credit.url}
                  >
                    <div className={`${styles.title} ${styles.bgTitle}`}>
                      {capitalize(displayedPicture.title)}
                    </div>
                    <span className={`${styles.author} ${styles.creditsTitle}`}>
                      {capitalize(displayedPicture.credit.title)}
                    </span>
                  </a>
                ))
              )}
            </div>
          </Fragment>
        )}
        <div className={cn(styles.hidingWrapper, hideUi && styles.hideElement)}>
          <div ref={popupRef}>
            <div className={styles.customizeContainer}>
              <button
                className={cn(
                  styles.customizeButtonExtended,
                  CUSTOMIZE_ALTERNATIVE_LOOK && styles.alternativeLook,
                  customizeClicked ? '' : styles.highlighted
                )}
                onClick={handleCustomizeClick}
              >
                <CustomizeIcon className={styles.customizeIcon} />
                <span>{buttonLabel}</span>
              </button>

              <button
                className={`${styles.customizeButtonExtended}`}
                onClick={setRandomBackground}
              >
                <SyncIcon
                  className={cn(
                    styles.customizeIcon,
                    isUpdating && styles.rotate
                  )}
                />
              </button>
            </div>

            {customizeOpened && (
              <Suspense fallback={null}>
                <CustomizePopup
                  onClose={handleCustomizeClick}
                  goBack={goBack}
                />
              </Suspense>
            )}
          </div>

          {children}
        </div>
      </div>

      <ErrorBoundary>
        <News />
      </ErrorBoundary>
    </>
  );
};

export default Ui;
